import { agentV2SourceKey } from '@/agent-v2/references/agent-v2-source-key'
import { labelAgentV2Citations } from '@/agent-v2/references/agent-v2-citation-labels'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'

/**
 * One reference (with its bespoke support) inside a section group.
 *
 * Cited groups take each citation id's chip label from its first-occurrence label
 * in labelAgentV2Citations — the same walk that labels marker badges and legal-link
 * hover cards — so a chip matches the number a reader sees for that passage. Related
 * groups have no inline occurrences; their supports label "N", "N.1", "N.2", ... in
 * registration order. Occurrences of one citation id share a single support, so
 * repeats never mint extra chips.
 */
export type AgentV2ReferenceSectionSupport = {
  reference: AgentV2Reference
  chipLabel: string
}

/**
 * One source document row in a message's references section. `supports` holds all of
 * the group's references in appearance order; the first is the primary — it carries
 * the display fields and is the row title's click-through target.
 */
export type AgentV2ReferenceSectionGroup = {
  /** agentV2SourceKey — the same grouping key labelAgentV2Citations numbers by. */
  sourceKey: string
  footnoteNumber: number
  supports: AgentV2ReferenceSectionSupport[]
}

export type AgentV2ReferenceSections = {
  citedGroups: AgentV2ReferenceSectionGroup[]
  relatedGroups: AgentV2ReferenceSectionGroup[]
}

/**
 * Derive the cited/related reference groups for one settled assistant message.
 *
 * Cited: sources resolved from the citation ids in the message text, one group per
 * source document (agentV2SourceKey), numbered per the labeler's marker-first rule
 * (marker-cited sources 1..M in citation order, link-only sources after) with each
 * distinct citation id appended to its group's supports. Groups list in number order.
 * Section numbers and chip labels both come from labelAgentV2Citations (unresolved
 * ids never consume a number). Marker chips match the inline badges, including gaps
 * from a repeated id. Legal-link chips use the suffix the labeler assigns after
 * those marker labels, the same number the hover card shows.
 *
 * Related: the turn's remaining references — `turn_id` matches and the source
 * document is not already cited — grouped the same way in reference registration
 * order (the map's insertion order), numbered continuing after the cited groups.
 *
 * Everything derives from the message text plus the conversation reference map;
 * no usage state is persisted anywhere.
 */
export function buildAgentV2ReferenceSections(params: {
  messageText: string
  turnId: string
  referencesByCitationId: Record<string, AgentV2Reference>
}): AgentV2ReferenceSections {
  const { messageText, turnId, referencesByCitationId } = params

  const citedGroups: AgentV2ReferenceSectionGroup[] = []
  const citedGroupBySourceKey = new Map<string, AgentV2ReferenceSectionGroup>()
  const citedCitationIds = new Set<string>()

  // The labeler numbers every marker occurrence, including repeats of an already-seen
  // citation id, while each id contributes one support carrying its first-occurrence
  // label. That keeps chips identical to badges and hover cards even when repeats
  // create label gaps (text A, A, B in one source chips "1" and "1.2", never "1.1").
  for (const citation of labelAgentV2Citations(messageText, referencesByCitationId)) {
    if (!citation.isResolved || citation.label === null || citation.footnoteNumber === null) continue
    if (citedCitationIds.has(citation.citationId)) continue
    citedCitationIds.add(citation.citationId)

    const reference = referencesByCitationId[citation.citationId]
    if (!reference) continue

    const sourceKey = agentV2SourceKey(reference)
    let group = citedGroupBySourceKey.get(sourceKey)
    if (!group) {
      group = { sourceKey, footnoteNumber: citation.footnoteNumber, supports: [] }
      citedGroupBySourceKey.set(sourceKey, group)
      citedGroups.push(group)
    }
    group.supports.push({ reference, chipLabel: citation.label })
  }
  citedGroups.sort((a, b) => a.footnoteNumber - b.footnoteNumber)

  // Cited numbers are contiguous 1..K by the labeler's construction; related continue after.
  let nextFootnoteNumber = citedGroups.length + 1

  const relatedGroups: AgentV2ReferenceSectionGroup[] = []
  const relatedGroupBySourceKey = new Map<string, AgentV2ReferenceSectionGroup>()

  for (const reference of Object.values(referencesByCitationId)) {
    if (reference.turn_id !== turnId) continue
    if (citedCitationIds.has(reference.citation_id)) continue

    const sourceKey = agentV2SourceKey(reference)
    // An uncited reference into an already-cited source document stays hidden.
    if (citedGroupBySourceKey.has(sourceKey)) continue

    let group = relatedGroupBySourceKey.get(sourceKey)
    if (!group) {
      group = { sourceKey, footnoteNumber: nextFootnoteNumber, supports: [] }
      nextFootnoteNumber += 1
      relatedGroupBySourceKey.set(sourceKey, group)
      relatedGroups.push(group)
    }
    group.supports.push({ reference, chipLabel: nextRelatedChipLabel(group) })
  }

  return { citedGroups, relatedGroups }
}

/** Related groups have no inline occurrences to inherit labels from: "N", "N.1", ... */
function nextRelatedChipLabel(group: AgentV2ReferenceSectionGroup): string {
  return group.supports.length === 0 ? `${group.footnoteNumber}` : `${group.footnoteNumber}.${group.supports.length}`
}
